<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { CockpitOps } from '@/types/cockpit'

const props = defineProps<{
  ops: CockpitOps | null
  loading: boolean
  error: string | null
}>()

const emit = defineEmits<{
  retry: []
}>()

const { t } = useI18n()

const successRatePercent = computed(() => {
  if (props.ops?.payment_success_rate === null || props.ops?.payment_success_rate === undefined) return null
  return (props.ops.payment_success_rate * 100).toFixed(1) + '%'
})

const successRateTone = computed(() => {
  const rate = props.ops?.payment_success_rate
  if (rate === null || rate === undefined) return 'neutral'
  if (rate >= 0.9) return 'good'
  if (rate >= 0.75) return 'warn'
  return 'bad'
})

const queueTone = computed(() => {
  const backlog = props.ops?.queue_backlog ?? 0
  return backlog > 50 ? 'warn' : 'neutral'
})

const failedJobsTone = computed(() => {
  const failed = props.ops?.failed_jobs_24h ?? 0
  return failed > 0 ? 'bad' : 'neutral'
})

const failedRefundsTone = computed(() => {
  const failed = props.ops?.failed_refunds ?? 0
  return failed > 0 ? 'bad' : 'neutral'
})
</script>

<template>
  <!-- Loading skeletons -->
  <div v-if="loading" class="grid grid-cols-2 md:grid-cols-4 gap-4">
    <div
      v-for="i in 5"
      :key="i"
      class="bg-slate-800 border border-slate-700 rounded-2xl p-5 animate-pulse"
    >
      <div class="h-3 bg-slate-700 rounded w-1/2 mb-3" />
      <div class="h-7 bg-slate-700 rounded w-2/3" />
    </div>
  </div>

  <!-- Error state -->
  <div
    v-else-if="error"
    class="bg-red-500/10 border border-red-500/30 rounded-2xl p-5 flex items-center justify-between"
  >
    <p class="text-red-300 text-sm">{{ t(error) }}</p>
    <button
      type="button"
      class="text-sm text-red-300 hover:text-red-200 font-medium underline"
      @click="emit('retry')"
    >
      {{ t('admin.common.retry') }}
    </button>
  </div>

  <!-- Ops tiles -->
  <div v-else-if="ops" class="grid grid-cols-2 md:grid-cols-4 gap-4">
    <!-- Queue backlog -->
    <div class="bg-slate-800 border border-slate-700 rounded-2xl p-5">
      <p class="text-slate-400 text-xs font-medium uppercase tracking-wide mb-2">
        {{ t('admin.cockpit.ops.queue_backlog') }}
      </p>
      <p
        class="text-2xl font-bold"
        :class="queueTone === 'warn' ? 'text-amber-400' : 'text-white'"
      >
        {{ ops.queue_backlog.toLocaleString() }}
      </p>
    </div>

    <!-- Failed jobs (24h) -->
    <div class="bg-slate-800 border border-slate-700 rounded-2xl p-5">
      <p class="text-slate-400 text-xs font-medium uppercase tracking-wide mb-2">
        {{ t('admin.cockpit.ops.failed_jobs_24h') }}
      </p>
      <p
        class="text-2xl font-bold"
        :class="failedJobsTone === 'bad' ? 'text-red-400' : 'text-white'"
      >
        {{ ops.failed_jobs_24h.toLocaleString() }}
      </p>
    </div>

    <!-- Pending payments -->
    <div class="bg-slate-800 border border-slate-700 rounded-2xl p-5">
      <p class="text-slate-400 text-xs font-medium uppercase tracking-wide mb-2">
        {{ t('admin.cockpit.ops.pending_payments') }}
      </p>
      <p class="text-2xl font-bold text-white">
        {{ ops.pending_payments.toLocaleString() }}
      </p>
    </div>

    <!-- Payment success rate -->
    <div class="bg-slate-800 border border-slate-700 rounded-2xl p-5">
      <p class="text-slate-400 text-xs font-medium uppercase tracking-wide mb-2">
        {{ t('admin.cockpit.ops.payment_success_rate') }}
      </p>
      <p
        class="text-2xl font-bold"
        :class="{
          'text-emerald-400': successRateTone === 'good',
          'text-amber-400': successRateTone === 'warn',
          'text-red-400': successRateTone === 'bad',
          'text-slate-400': successRateTone === 'neutral',
        }"
      >
        {{ successRatePercent ?? '—' }}
      </p>
    </div>

    <!-- Failed refunds (#157) -->
    <div class="bg-slate-800 border border-slate-700 rounded-2xl p-5">
      <p class="text-slate-400 text-xs font-medium uppercase tracking-wide mb-2">
        {{ t('admin.cockpit.ops.failed_refunds') }}
      </p>
      <p
        class="text-2xl font-bold"
        :class="failedRefundsTone === 'bad' ? 'text-red-400' : 'text-white'"
      >
        {{ ops.failed_refunds.toLocaleString() }}
      </p>
    </div>

    <!-- NOTE: No webhook failure tile — data not available in Phase 1 (§8.5) -->
  </div>
</template>
